import React, { useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Brain, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'Privacy Policy - MIND FRAME';
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-teal-50">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-200">
        <nav className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-teal-600 rounded-lg flex items-center justify-center">
              <Brain className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-bold text-slate-800">MIND FRAME</span>
          </div>
          <Button onClick={() => navigate('/')} variant="outline" className="gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Button>
        </nav>
      </header>

      {/* Content */}
      <div className="pt-32 pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl p-8 md:p-12 border border-blue-100">
            <h1 className="text-4xl font-bold text-slate-800 mb-4">MIND FRAME – Privacy Policy</h1>
            <p className="text-slate-600 mb-8"><strong>Effective Date:</strong> March 26th, 2026</p>
            
            <p className="text-slate-700 leading-relaxed mb-8">
              MIND FRAME ("we", "our", "us") is committed to protecting your privacy and ensuring the confidentiality of your personal and sensitive information.
            </p>

            <hr className="my-8 border-slate-200" />

            {/* Section 1 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-800 mb-4">1. Information We Collect</h2>
              <p className="text-slate-700 mb-4">We may collect the following types of information:</p>
              
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-slate-800 mb-2">a) Personal Information</h3>
                  <ul className="list-disc list-inside space-y-1 text-slate-700 ml-4">
                    <li>Name, email address, phone number</li>
                    <li>Age and demographic details</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-slate-800 mb-2">b) Sensitive Information</h3>
                  <ul className="list-disc list-inside space-y-1 text-slate-700 ml-4">
                    <li>Information shared during counselling sessions</li>
                    <li>Mental health-related concerns and history</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-slate-800 mb-2">c) Technical Information</h3>
                  <ul className="list-disc list-inside space-y-1 text-slate-700 ml-4">
                    <li>IP address, device type, browser type</li>
                    <li>Usage data within the app</li>
                  </ul>
                </div>
              </div>
            </section>

            <hr className="my-8 border-slate-200" />

            {/* Section 2 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-800 mb-4">2. How We Use Your Information</h2>
              <p className="text-slate-700 mb-3">We use your information to:</p>
              <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
                <li>Provide counselling services</li>
                <li>Schedule and manage appointments</li>
                <li>Improve user experience</li>
                <li>Communicate with you regarding sessions</li>
              </ul>
            </section>

            <hr className="my-8 border-slate-200" />

            {/* Section 3 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-800 mb-4">3. Confidentiality of Sessions</h2>
              <p className="text-slate-700 mb-3">All counselling sessions are treated with strict confidentiality.</p>
              <p className="text-slate-700 mb-3">However, confidentiality may be limited in the following cases:</p>
              <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
                <li>Risk of harm to self or others</li>
                <li>Legal obligations (court orders, law enforcement requests)</li>
                <li>Cases involving abuse or neglect</li>
              </ul>
            </section>

            <hr className="my-8 border-slate-200" />

            {/* Section 4 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-800 mb-4">4. Data Storage & Security</h2>
              <p className="text-slate-700">
                We implement reasonable security measures to protect your data. However, no digital platform can guarantee absolute security.
              </p>
            </section>

            <hr className="my-8 border-slate-200" />

            {/* Section 5 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-800 mb-4">5. Sharing of Information</h2>
              <p className="text-slate-700 mb-3">We do not sell or share your personal data with third parties, except:</p>
              <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
                <li>When required by law</li>
                <li>To protect safety and legal rights</li>
              </ul>
            </section>

            <hr className="my-8 border-slate-200" />

            {/* Section 6 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-800 mb-4">6. Online Session Disclaimer</h2>
              <p className="text-slate-700 mb-3">Online counselling involves certain risks, including:</p>
              <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
                <li>Technical disruptions</li>
                <li>Potential privacy breaches</li>
              </ul>
              <p className="text-slate-700 mt-3">
                By using our services, you acknowledge and accept these risks.
              </p>
            </section>

            <hr className="my-8 border-slate-200" />

            {/* Section 7 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-800 mb-4">7. Your Rights</h2>
              <p className="text-slate-700 mb-3">You may:</p>
              <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
                <li>Request access to your data</li>
                <li>Request correction or deletion</li>
                <li>Withdraw consent at any time</li>
              </ul>
            </section>

            <hr className="my-8 border-slate-200" />

            {/* Section 8 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-800 mb-4">8. Data Retention</h2>
              <p className="text-slate-700">
                We retain your data only as long as necessary for service provision or legal compliance.
              </p>
            </section>

            <hr className="my-8 border-slate-200" />

            {/* Section 9 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-800 mb-4">9. Changes to Policy</h2>
              <p className="text-slate-700">
                We reserve the right to update this Privacy Policy. Updates will be reflected on this page.
              </p>
            </section>

            <hr className="my-8 border-slate-200" />

            {/* Section 10 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-800 mb-4">10. Contact</h2>
              <p className="text-slate-700">
                For any concerns regarding privacy:<br />
                Email: <a href="mailto:gaurav@mfcounsellingservices.com" className="text-blue-600 hover:text-blue-700 underline">gaurav@mfcounsellingservices.com</a>
              </p>
            </section>

            <hr className="my-8 border-slate-200" />

            {/* Footer */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
              <p className="text-slate-800 font-medium">
                By using MIND FRAME, you consent to this Privacy Policy.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-8 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-slate-400">© 2024 MIND FRAME. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
